import { G } from '@mobily/ts-belt';
import { Decimal } from 'decimal.js';
// eslint-disable-next-line import-x/extensions -- it tries and fails importing ESM otherwise
import { read, utils } from 'xlsx/dist/xlsx.full.min.js';

import {
  CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
  CAMPAIGN_ALLOCATION_FILE_MAX_LINE,
  CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION,
  CAMPAIGN_VACATION_DAYS_TYPE_FILE_LABELS,
} from '@Common/constants/b2b/campaigns';
import {
  ZodXlsxAllocationsDaysData,
  ZodXlsxAllocationsVacationDaysDistributorData,
} from '@Common/types/bo/b2b/employees';
import { isZodError } from '@Common/types/utils';

import {
  AllocationsFileError,
  convertZodErrorToAllocationsFileError,
  isVersionValid,
  rejectDuplicateEmployeeIdentifiers,
  rejectNegativeAmounts,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/utils';
import { VACATION_DAYS_FILE_COLUMNS } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysColumns';
import { formatValue, parseValue } from '@sh4res/bo-common/fields';

export const VACATION_DAYS_FIELDS = [
  'cet_days',
  'rtt_days',
  'overtime_days',
  'paid_leave_days',
  'seniority_leave_days',
  'untaken_rest_days',
] as const;

export const hasAnyVacationDays = (
  row: {
    [K in (typeof VACATION_DAYS_FIELDS)[number]]?: number | null;
  },
) => VACATION_DAYS_FIELDS.some((field) => (row[field] ?? 0) > 0);

const parseDaysCell = (value: string | number | undefined) =>
  value === undefined || value === '' ? null : formatValue(parseValue(Number(value)));

export const parseFileVacationDays = async ({ file }: { file: File }) => {
  const fileData = await file.arrayBuffer();
  const workbook = read(new Uint8Array(fileData), { type: 'array' });

  const [sheetName] = workbook.SheetNames;
  const sheet = workbook.Sheets[sheetName];

  if (!isVersionValid(workbook, CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION)) {
    throw new AllocationsFileError(
      `Invalid file version, actual is v${CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION}`,
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_file_version',
      { version: CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION },
    );
  }

  const rawData = utils.sheet_to_json<Record<string, string | number>>(sheet, {
    range: `A${CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE - 1}:AZ${CAMPAIGN_ALLOCATION_FILE_MAX_LINE}`,
  });

  try {
    const parsed = ZodXlsxAllocationsDaysData.parse(
      rawData.map((entry) => {
        const daysByType = Object.fromEntries(
          Object.entries(VACATION_DAYS_FILE_COLUMNS).map(([field, column]) => [
            field,
            parseDaysCell(entry[column]),
          ]),
        );
        return {
          nir: entry['NIR*'] ?? null,
          ntt: entry.NTT,
          firstname: entry['Prénom'],
          lastname: entry.Nom,
          ...daysByType,
        };
      }),
    );
    rejectDuplicateEmployeeIdentifiers(parsed);
    return parsed;
  } catch (error) {
    if (isZodError(error)) {
      throw convertZodErrorToAllocationsFileError(
        error,
        CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
      );
    }
    throw error;
  }
};

type EmployeeIdentifierRow = { nir?: string | null; ntt?: string | null };

// Rows sharing a NIR or an NTT belong to the same employee, even when one row only has the NIR and
// another only the NTT. A row without any identifier is its own employee, it is never merged.
const getEmployeeGroupIndexes = (rows: EmployeeIdentifierRow[]) => {
  const parents = rows.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parents[root] !== root) {
      root = parents[root];
    }
    parents[index] = root;
    return root;
  };

  const firstIndexByIdentifier = new Map<string, number>();
  for (const [index, row] of rows.entries()) {
    for (const identifier of [
      row.nir ? `nir:${row.nir}` : null,
      row.ntt ? `ntt:${row.ntt}` : null,
    ]) {
      if (G.isNullable(identifier)) {
        continue;
      }
      const firstIndex = firstIndexByIdentifier.get(identifier);
      if (G.isNullable(firstIndex)) {
        firstIndexByIdentifier.set(identifier, index);
      } else {
        parents[find(index)] = find(firstIndex);
      }
    }
  }

  return rows.map((_, index) => find(index));
};

const rejectDuplicateEmployeeDayTypes = (
  rows: (EmployeeIdentifierRow & { days_type: string })[],
  groupIndexes: number[],
) => {
  const firstLineByKey = new Map<string, number>();
  for (const [index, row] of rows.entries()) {
    const key = `${groupIndexes[index]}|${row.days_type}`;
    const line = index + CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE;
    const firstLine = firstLineByKey.get(key);
    if (G.isNotNullable(firstLine)) {
      const identifier = row.nir ?? row.ntt ?? '';
      throw new AllocationsFileError(
        `Duplicate employee identifier ${identifier} for day type ${row.days_type} on lines ${firstLine} and ${line}`,
        'resources.bo_common.b2b.campaigns.sections.allocations.errors.duplicate_employee',
        { identifier, firstLine: String(firstLine), line: String(line) },
      );
    }
    firstLineByKey.set(key, line);
  }
};

const dayTypeByFileLabel = new Map<string, string>(
  Object.entries(CAMPAIGN_VACATION_DAYS_TYPE_FILE_LABELS).map(([dayType, label]) => [
    label,
    dayType,
  ]),
);

// A row with days to invest must say which type of days they are
const rejectMissingDayTypes = (rows: Record<string, string | null>[]) => {
  for (const [index, row] of rows.entries()) {
    const vacationDays = row['Nombre de jours à investir*'];
    const hasDays = G.isNotNullable(vacationDays) && vacationDays !== '';
    const dayTypeLabel = row['Type de jour*'];

    if (hasDays && (G.isNullable(dayTypeLabel) || String(dayTypeLabel).trim() === '')) {
      const line = index + CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE;
      throw new AllocationsFileError(
        `Missing day type on line ${line}`,
        'resources.bo_common.b2b.campaigns.sections.allocations.errors.missing_day_type',
        { line: String(line) },
      );
    }
  }
};

// The file has one row per employee and day type, grouped here into one allocation per employee
export const parseFileVacationDaysDistributor = async ({ file }: { file: File }) => {
  const fileData = await file.arrayBuffer();
  const workbook = read(new Uint8Array(fileData), { type: 'array' });

  const [sheetName] = workbook.SheetNames;
  const sheet = workbook.Sheets[sheetName];

  if (!isVersionValid(workbook, CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION)) {
    throw new AllocationsFileError(
      `Invalid file version, actual is v${CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION}`,
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_file_version',
      { version: CAMPAIGN_VACATION_DAYS_ALLOCATION_FILE_VERSION },
    );
  }

  const rawData = utils.sheet_to_json<Record<string, string | null>>(sheet, {
    range: `A${CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE - 1}:AZ${CAMPAIGN_ALLOCATION_FILE_MAX_LINE}`,
  });

  rejectMissingDayTypes(rawData);

  try {
    const parsed = ZodXlsxAllocationsVacationDaysDistributorData.parse(
      rawData
        .filter((entry) => {
          const vacationDays = entry['Nombre de jours à investir*'];
          return G.isNotNullable(vacationDays) && vacationDays !== '';
        })
        .map((entry) => {
          const amount = entry['Montant net de CSG/CRDS*'];
          const vacationDays = entry['Nombre de jours à investir*'];
          const dayTypeLabel = entry['Type de jour*']?.trim();

          return {
            nir: entry['NIR*'] ?? null,
            ntt: entry.NTT,
            firstname: entry['Prénom'],
            lastname: entry.Nom,
            // An unknown label is kept as is so that validation reports it with its line
            days_type: G.isNotNullable(dayTypeLabel)
              ? (dayTypeByFileLabel.get(dayTypeLabel) ?? dayTypeLabel)
              : undefined,
            amount: amount ? formatValue(parseValue(Number(amount))) : null,
            amount_in_days: vacationDays ? formatValue(parseValue(Number(vacationDays))) : null,
          };
        }),
    );
    const groupIndexes = getEmployeeGroupIndexes(parsed);
    rejectDuplicateEmployeeDayTypes(parsed, groupIndexes);
    rejectNegativeAmounts(parsed);

    const rowsByGroup = new Map<number, typeof parsed>();
    for (const [index, row] of parsed.entries()) {
      rowsByGroup.set(groupIndexes[index], [...(rowsByGroup.get(groupIndexes[index]) ?? []), row]);
    }
    return [...rowsByGroup.values()].map((employeeRows) => {
      const [{ days_type: _daysType, amount_in_days: _amountInDays, ...employee }] = employeeRows;
      return {
        ...employee,
        amount: employeeRows
          .reduce((total, row) => total.plus(row.amount ?? 0), new Decimal(0))
          .toNumber(),
        day_type_allocations: employeeRows.map((row) => ({
          days_type: row.days_type,
          amount: row.amount,
          days: row.amount_in_days,
        })),
      };
    });
  } catch (error) {
    if (isZodError(error)) {
      throw convertZodErrorToAllocationsFileError(
        error,
        CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
      );
    }
    throw error;
  }
};
